import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Search, Save, Settings } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSET_CLASSES = [
  'Equities',
  'Fixed Income',
  'Real Estate',
  'Commodities',
  'Mutual Funds',
  'Gold',
  'Cash',
  'Others'
];

const PortfolioThresholds = ({ clientId, clientName, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assetClassLimits, setAssetClassLimits] = useState([]);
  const [securityLimits, setSecurityLimits] = useState([]);
  const [thresholdType, setThresholdType] = useState('percentage');
  const [thresholdValue, setThresholdValue] = useState('');
  
  // Security search
  const [securitySearch, setSecuritySearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchThresholds();
  }, [clientId]);

  useEffect(() => {
    if (securitySearch.length >= 2) {
      searchSecurities();
    } else {
      setSearchResults([]);
    }
  }, [securitySearch]);

  const fetchThresholds = async () => {
    setLoading(true);
    try {
      const config = { withCredentials: true };

      const response = await axios.get(`${API}/portfolio-thresholds/${clientId}`, config);
      
      if (response.data) {
        setAssetClassLimits(response.data.asset_class_limits || []);
        setSecurityLimits(response.data.security_limits || []);
        setThresholdType(response.data.threshold_type || 'percentage');
        setThresholdValue(response.data.threshold_value || '');
      }
    } catch (error) {
      console.error('Error fetching thresholds:', error);
      toast.error('Failed to fetch thresholds');
    } finally {
      setLoading(false);
    }
  };

  const searchSecurities = async () => {
    setSearching(true);
    try {
      const config = { withCredentials: true };

      const response = await axios.get(`${API}/securities/search?q=${securitySearch}`, config);
      setSearchResults(response.data.securities || []);
    } catch (error) {
      console.error('Error searching securities:', error);
    } finally {
      setSearching(false);
    }
  };

  const addAssetClassLimit = () => {
    setAssetClassLimits([
      ...assetClassLimits,
      { asset_class: '', min_allocation: 0, max_allocation: 0 }
    ]);
  };

  const updateAssetClassLimit = (index, field, value) => {
    const updated = [...assetClassLimits];
    updated[index][field] = value;
    setAssetClassLimits(updated);
  };

  const removeAssetClassLimit = (index) => {
    setAssetClassLimits(assetClassLimits.filter((_, i) => i !== index));
  };

  const addSecurityLimit = (security) => {
    // Check if already added
    if (securityLimits.find(s => s.security_id === security.id)) {
      toast.error('Security already added');
      return;
    }
    
    setSecurityLimits([
      ...securityLimits,
      { 
        security_id: security.id, 
        security_name: security.security_name,
        max_exposure: 0 
      }
    ]);
    setSecuritySearch('');
    setSearchResults([]);
  };

  const updateSecurityLimit = (index, value) => {
    const updated = [...securityLimits];
    updated[index].max_exposure = value;
    setSecurityLimits(updated);
  };

  const removeSecurityLimit = (index) => {
    setSecurityLimits(securityLimits.filter((_, i) => i !== index));
  };

  const saveThresholds = async () => {
    setSaving(true);
    try {
      const config = { withCredentials: true };

      const payload = {
        client_id: clientId,
        asset_class_limits: assetClassLimits.filter(l => l.asset_class),
        security_limits: securityLimits,
        threshold_type: thresholdType,
        threshold_value: thresholdValue ? parseFloat(thresholdValue) : null,
        threshold_currency: 'INR'
      };
      
      await axios.post(`${API}/portfolio-thresholds`, payload, config);
      
      toast.success('Portfolio thresholds saved successfully');
      if (onClose) onClose();
    } catch (error) {
      console.error('Error saving thresholds:', error);
      toast.error('Failed to save thresholds');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ugmonk-yellow"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Portfolio Thresholds</h2>
          <p className="text-gray-600">Set limits for {clientName}</p>
        </div>
        <Button
          onClick={saveThresholds}
          disabled={saving}
          className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Thresholds'}
        </Button>
      </div>

      {/* Asset Class Allocation Limits */}
      <Card className="border-gray-200 bg-white">
        <CardHeader>
          <CardTitle className="text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5 text-ugmonk-dark" />
            1. Asset Class Allocation Limits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {assetClassLimits.map((limit, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-gray-200 rounded-lg">
              <div>
                <Label className="text-gray-700">Asset Class</Label>
                <select
                  value={limit.asset_class}
                  onChange={(e) => updateAssetClassLimit(index, 'asset_class', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
                >
                  <option value="">Select...</option>
                  {ASSET_CLASSES.map(ac => (
                    <option key={ac} value={ac}>{ac}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-gray-700">Minimum Allocation (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={limit.min_allocation}
                  onChange={(e) => updateAssetClassLimit(index, 'min_allocation', parseFloat(e.target.value))}
                  className="bg-white border-gray-300"
                />
              </div>
              <div>
                <Label className="text-gray-700">Maximum Allocation (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={limit.max_allocation}
                  onChange={(e) => updateAssetClassLimit(index, 'max_allocation', parseFloat(e.target.value))}
                  className="bg-white border-gray-300"
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeAssetClassLimit(index)}
                  className="border-red-500 text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
          
          <Button
            variant="outline"
            onClick={addAssetClassLimit}
            className="border-ugmonk-yellow text-ugmonk-dark hover:bg-[#fffdf0]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Asset Class
          </Button>
        </CardContent>
      </Card>

      {/* Security Exposure Limits */}
      <Card className="border-gray-200 bg-white">
        <CardHeader>
          <CardTitle className="text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5 text-ugmonk-yellow" />
            2. Security Exposure Limits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Box */}
          <div className="relative">
            <Label className="text-gray-700">Search and Add Security</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by name, ticker, or ISIN..."
                value={securitySearch}
                onChange={(e) => setSecuritySearch(e.target.value)}
                className="pl-10 bg-white border-gray-300"
              />
            </div>
            
            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {searchResults.map((security) => (
                  <div
                    key={security.id}
                    onClick={() => addSecurityLimit(security)}
                    className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">{security.security_name}</div>
                    <div className="text-sm text-gray-600">
                      {security.ticker} • {security.asset_class}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Added Securities */}
          <div className="space-y-2">
            {securityLimits.map((limit, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border border-gray-200 rounded-lg">
                <div className="md:col-span-2">
                  <Label className="text-gray-700">Security</Label>
                  <div className="font-medium text-gray-900">{limit.security_name}</div>
                </div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-gray-700">Max Exposure (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={limit.max_exposure}
                      onChange={(e) => updateSecurityLimit(index, parseFloat(e.target.value))}
                      className="bg-white border-gray-300"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => removeSecurityLimit(index)}
                    className="border-red-500 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {securityLimits.length === 0 && (
            <p className="text-center py-4 text-gray-500">
              Search and add securities to set exposure limits
            </p>
          )}
        </CardContent>
      </Card>

      {/* Threshold Reference */}
      <Card className="border-gray-200 bg-white">
        <CardHeader>
          <CardTitle className="text-gray-900 flex items-center gap-2">
            <Settings className="w-5 h-5 text-ugmonk-dark" />
            3. Threshold Reference
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-gray-700">Threshold Type</Label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="percentage"
                  checked={thresholdType === 'percentage'}
                  onChange={(e) => setThresholdType(e.target.value)}
                  className="w-4 h-4 text-ugmonk-yellow"
                />
                <span className="text-gray-900">Percentage of total portfolio</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="absolute"
                  checked={thresholdType === 'absolute'}
                  onChange={(e) => setThresholdType(e.target.value)}
                  className="w-4 h-4 text-ugmonk-yellow"
                />
                <span className="text-gray-900">Absolute amount (₹)</span>
              </label>
            </div>
          </div>

          <div>
            <Label className="text-gray-700">
              {thresholdType === 'percentage' ? 'Threshold Value (%)' : 'Threshold Value (₹)'}
            </Label>
            <Input
              type="number"
              min="0"
              value={thresholdValue}
              onChange={(e) => setThresholdValue(e.target.value)}
              placeholder={thresholdType === 'percentage' ? 'Enter percentage' : 'Enter amount in ₹'}
              className="bg-white border-gray-300"
            />
            <p className="text-sm text-gray-500 mt-1">
              {thresholdType === 'percentage' 
                ? 'Alerts trigger when allocation deviates by this percentage'
                : 'Alerts trigger when position value exceeds this amount'}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortfolioThresholds;
